import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { catchError, switchMap, Observable, throwError } from 'rxjs';

/**
 * Interceptor
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  // Chatbot API is public — exempt by both configured host AND by path so that
  // dynamic source-param base URLs (e.g. /chat?source=...) never receive the
  // platform bearer token, regardless of which host the request targets.
  const requestPath = new URL(req.url, window.location.origin).pathname;
  const isChatbotApiRequest =
    requestPath.includes('/chatbot/initialization-flow/configs') ||
    requestPath.includes('/chatbot/flows/') ||
    requestPath.includes('/chatbot/executions/');
  if (isChatbotApiRequest) {
    return next(req);
  }

  const authReq = authService.accessToken
    ? req.clone({
        headers: req.headers.set('Authorization', `Bearer ${authService.accessToken}`),
      })
    : req;

  // A 401 on a guest reset-password call means a bad OTP/challenge, not an expired
  // session — refreshing would sign the user out and hide the API's error message.
  const isGuestResetPasswordRequest =
    requestPath.endsWith('/reset-password') || requestPath.includes('/reset-password/challenges/');

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !req.url.endsWith('/auth/refresh') && !isGuestResetPasswordRequest) {
        return handle401Error(req, next, authService);
      }
      return throwError(() => error);
    })
  );
};

/**
 * Handle 401 Error
 *
 * @param req
 * @param next
 * @param authService
 * @param router
 * @returns {Observable<HttpEvent<unknown>>}
 */
function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<unknown>> {
  return authService.refreshTokens().pipe(
    switchMap((token: string) => {
      if (!token) {
        authService.signOut();
        return throwError(() => new Error('Token refresh failed'));
      }

      const clonedReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`),
      });
      return next(clonedReq);
    }),
    catchError(() => {
      authService.signOut();
      return throwError(() => new Error('Session expired and token refresh failed.'));
    })
  );
}
